// react
import type { ReactNode } from 'react';
// libraries
import type {
  Control,
  FieldPath,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';
import { Controller } from 'react-hook-form';

// material ui
import { Box, TextField } from '@mui/material';
import type { TextFieldProps } from '@mui/material';

// relative
import RHFFieldLabel from './RHFFieldLabel';

export type RHFNumberFieldProps<TFieldValues extends FieldValues> = {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
  label?: ReactNode;
  rules?: UseControllerProps<TFieldValues>['rules'];
} & Omit<
  TextFieldProps,
  'name' | 'defaultValue' | 'label' | 'error' | 'type' | 'onChange' | 'value'
>;

// Keeps the underlying react-hook-form value as a `number | null` while the
// MUI TextField always displays/edits a string, avoiding uncontrolled ->
// controlled warnings on empty input.
export default function RHFNumberField<TFieldValues extends FieldValues>({
  name,
  control,
  label,
  rules,
  required,
  id,
  ...textFieldProps
}: RHFNumberFieldProps<TFieldValues>) {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const inputId = id || field.name;

        return (
          <Box>
            {label ? (
              <RHFFieldLabel htmlFor={inputId} required={required}>
                {label}
              </RHFFieldLabel>
            ) : null}
            <TextField
              {...field}
              {...textFieldProps}
              id={inputId}
              required={required}
              type="number"
              variant="outlined"
              value={field.value ?? ''}
              onChange={(event) => {
                const rawValue = event.target.value;
                field.onChange(rawValue === '' ? null : Number(rawValue));
              }}
              error={!!fieldState.error}
              helperText={
                fieldState.error?.message ?? textFieldProps.helperText
              }
            />
          </Box>
        );
      }}
    />
  );
}
